import {animate, state, style, transition, trigger} from '@angular/animations';
import {Dialog} from '@angular/cdk/dialog';
import {CommonModule} from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    Injector,
    OnInit,
    QueryList,
    Type,
    ViewChild,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {FilterService} from '@truelearn-web/shared/filter';
import {combineLatest, Observable, of} from 'rxjs';
import {filter, switchMap, take, tap} from 'rxjs/operators';

import {InstitutionService} from 'libs/data-access/institution/src/lib/services/institution.service';
import {InstitutionContextService} from 'libs/data-access/institution/src/lib/services/institution-context.service';
import {GlobalLoaderService} from 'libs/shared/component/src/lib/service/global-loader.service';
import {DialogContainerComponent} from 'libs/shared/ui/src/lib/components/dialog-container/dialog-container.component';
import {NoFiltersComponent} from 'libs/shared/ui/src/lib/components/no-filter/no-filters.component';
import {TooltipDirective} from 'libs/shared/ui/src/lib/directives/tooltip/tooltip.directive';
import {SelectOption} from 'libs/shared/ui/src/lib/models/select-option.model';
import {CollapseDirective} from 'libs/shared/util/src/lib/directives/collapse.directive';
import {TruncatePipe} from 'libs/shared/util/src/lib/pipes/truncate.pipe';
import {
    InstitutionChangeModalComponent,
    InstitutionChangeModalData
} from './institution-change-modal/institution-change-modal.component';

// eslint-disable-next-line @typescript-eslint/naming-convention
const DEFAULT_EXPANDED_WIDTH = '15rem';

@Component({
    imports: [CommonModule, TooltipDirective, CollapseDirective, TruncatePipe],

    selector: 'app-secondary-sidebar',
    templateUrl: './secondary-sidebar.component.html',
    styleUrls: ['./secondary-sidebar.component.scss'],
    animations: [
        trigger('addMarginToContent', [
            state('add', style({'margin-left': '*'})),
            transition('add => subtract', [animate('350ms')]),
            state('subtract', style({'margin-left': '2.4rem'})),
            transition('subtract => add', [animate('350ms')])
        ])
    ]
})
export class SecondarySidebarComponent implements OnInit {
    @ViewChild('filterHost', {read: ViewContainerRef, static: true})
    filterHost: ViewContainerRef;
    @ViewChildren(CollapseDirective) collapsibles: QueryList<CollapseDirective>;
    @ViewChild('institutionDropdown', {static: false}) institutionDropdownRef: ElementRef;

    filterComponent: Type<unknown>;
    filterInjector: Injector;
    expandedWidth = DEFAULT_EXPANDED_WIDTH;
    expanded = true;
    institutionName$ = new Observable<string>();
    isToggleByUser = false;
    isOpen = true;

    institutionSelectData: Array<SelectOption> = [];
    selectedInstitution: SelectOption;
    isInstitutionDisabled$ = of(false);

    get isNotExpandable(): boolean {
        return this.filterComponent === NoFiltersComponent;
    }

    get shouldExpand(): boolean {
        return this.expanded && !this.isNotExpandable;
    }

    constructor(
        private filterService: FilterService,
        private changeDet: ChangeDetectorRef,
        private institutionContextService: InstitutionContextService,
        private institutionService: InstitutionService,
        private router: Router,
        private dialog: Dialog,
        private destroy: DestroyRef,
        private globalLoaderService: GlobalLoaderService,
        private viewContainerRef: ViewContainerRef
    ) {}

    async ngOnInit(): Promise<void> {
        combineLatest([
            this.institutionContextService.institutionsList$,
            this.institutionContextService.contextInstitutionInformation$
        ])
            .pipe(takeUntilDestroyed(this.destroy))
            .subscribe(([institutions, currentInstitution]) => {
                this.institutionSelectData = institutions.map((institution) => ({
                    id: institution.InstitutionId,
                    text: institution.InstitutionName
                }));

                const selected = this.institutionSelectData.find(
                    (item) => item.text === currentInstitution.InstitutionName
                );
                this.selectedInstitution = selected;
            });

        this.filterService.contextFilterComponent$
            .pipe(takeUntilDestroyed(this.destroy))
            .subscribe(({component, expanded, injector, width = DEFAULT_EXPANDED_WIDTH}) => {
                const widthChanged = width !== this.expandedWidth;
                this.filterComponent = component;
                this.filterInjector = injector;
                this.expandedWidth = width;
                this.expanded = expanded;
                this.changeDet.detectChanges();
                this.loadFilters();

                // The width only applies when the sidebar animates, so resize one that stays open.
                if (widthChanged && this.shouldExpand) {
                    this.collapsibles?.forEach((x) => x.expand());
                }
            });
    }

    toggleCollapse(): void {
        this.collapsibles.forEach((x) => x.toggle());
        this.expanded = !this.expanded;
        this.isToggleByUser = true;
        this.isOpen = !this.isOpen;
    }

    loadFilters(): void {
        // Clear out the secondary sidebar elements
        this.filterHost.clear();
        // Recreate the referenced component in whichever injector the page that registered it supplied
        this.filterHost.createComponent(this.filterComponent, {injector: this.filterInjector});

        this.changeDet.detectChanges();
    }

    onChangeInstitutionClick(): void {
        this.dialog
            .open<string, InstitutionChangeModalData>(InstitutionChangeModalComponent, {
                data: {
                    institutions: this.institutionSelectData,
                    selectedId: this.selectedInstitution?.id
                },
                panelClass: ['modal-md'],
                disableClose: true,
                ariaLabel: 'Change institution dialog',
                container: DialogContainerComponent
            })
            .closed.pipe(
                take(1),
                filter(
                    (selectedInstitutionId) =>
                        selectedInstitutionId && selectedInstitutionId !== this.selectedInstitution?.id
                ),
                tap(() => this.globalLoaderService.show(this.viewContainerRef)),
                switchMap((selectedInstitutionId) =>
                    this.institutionService.setFacultyInstitution(selectedInstitutionId)
                ),
                tap(() => {
                    let returnUrl = this.router.url.split('/').filter(Boolean);
                    const viewIndex = returnUrl.indexOf('view');
                    if (viewIndex !== -1) {
                        returnUrl = returnUrl.slice(0, viewIndex);
                    }
                    this.globalLoaderService.hide();
                    location.href = returnUrl.join('/');
                }),
                takeUntilDestroyed(this.destroy)
            )
            .subscribe();
    }
}
